import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { useIdleTimeContext } from "@labeling-tools/ui-components/src";

interface TimingData {
  total_duration: number;
  q1_duration: number;
  q2_duration: number;
  q3_duration: number;
  review_duration: number;
}

interface TimeTrackingContextValue {
  recordQ1Interaction: () => void;
  recordQ2Interaction: () => void;
  recordQ3Interaction: () => void;
  getTimingData: () => TimingData | null;
}

type Interval = { start: number; end: number };

/** Gap threshold: if a 1 s tick fires and this much wall-clock time has passed,
 *  the JS engine was suspended (computer sleep / aggressive throttle). */
const SLEEP_GAP_MS = 3_000;

/** How long the window must be blurred before we start counting it as away time.
 *  Ignores brief focus losses like clicking the URL bar or DevTools. */
const WINDOW_BLUR_GRACE_MS = 5_000;

/** Merge a sorted list of possibly-overlapping intervals into disjoint ones. */
function mergeIntervals(sorted: Interval[]): Interval[] {
  const result: Interval[] = [];
  for (const iv of sorted) {
    const last = result.at(-1);
    if (last && iv.start <= last.end) {
      last.end = Math.max(last.end, iv.end);
    } else {
      result.push({ ...iv });
    }
  }
  return result;
}

const TimeTrackingContext = createContext<TimeTrackingContextValue | null>(
  null,
);

/**
 * Must be rendered inside an IdleTimeProvider — reads isIdle directly from
 * IdleTimeContext rather than duplicating its activity-tracking logic.
 *
 * Pause sources tracked:
 *   1. isIdle (60 s inactivity)  — via IdleTimeContext
 *   2. Tab hidden / screen lock  — visibilitychange
 *   3. Computer sleep            — heartbeat gap detection
 *   4. Alt-tab to another app    — window blur/focus with grace period
 */
export function TimeTrackingProvider({
  taskId,
  children,
}: {
  taskId: string;
  children: ReactNode;
}) {
  const { isIdle } = useIdleTimeContext();

  const taskStartRef = useRef(Date.now());
  const q1CheckpointRef = useRef<number | null>(null);
  const q2CheckpointRef = useRef<number | null>(null);
  const q3CheckpointRef = useRef<number | null>(null);
  const q2FirstTouchedRef = useRef(false);
  const q3FirstTouchedRef = useRef(false);

  // Pause machine (idle + visibility)
  const isPausedRef = useRef(false);
  const pauseStartRef = useRef<number | null>(null);
  const pauseIntervalsRef = useRef<Interval[]>([]);

  // Sleep gap detection
  const lastTickRef = useRef(Date.now());

  // Window blur tracking (computed lazily at submit time)
  const blurStartRef = useRef<number | null>(null);

  // Stable ref so the visibilitychange handler always sees the latest isIdle
  // without needing to re-register on every idle state change.
  const isIdleRef = useRef(isIdle);
  useEffect(() => {
    isIdleRef.current = isIdle;
  }, [isIdle]);

  // Pause state machine: starts when idle OR tab hidden, ends when both clear.
  const updatePauseState = useCallback((shouldPause: boolean) => {
    if (shouldPause && !isPausedRef.current) {
      isPausedRef.current = true;
      pauseStartRef.current = Date.now();
    } else if (!shouldPause && isPausedRef.current) {
      if (pauseStartRef.current !== null) {
        pauseIntervalsRef.current.push({
          start: pauseStartRef.current,
          end: Date.now(),
        });
      }
      isPausedRef.current = false;
      pauseStartRef.current = null;
    }
  }, []);

  // Reset all tracking state when a new task loads.
  useEffect(() => {
    taskStartRef.current = Date.now();
    q1CheckpointRef.current = null;
    q2CheckpointRef.current = null;
    q3CheckpointRef.current = null;
    q2FirstTouchedRef.current = false;
    q3FirstTouchedRef.current = false;
    pauseIntervalsRef.current = [];
    lastTickRef.current = Date.now();
    blurStartRef.current = null;
    // Preserve current idle/visibility state so we don't miss an open pause.
    const paused = isIdleRef.current || document.hidden;
    isPausedRef.current = paused;
    pauseStartRef.current = paused ? Date.now() : null;
  }, [taskId]);

  // Sync isIdle changes from IdleTimeContext → pause machine.
  useEffect(() => {
    updatePauseState(isIdle || document.hidden);
  }, [isIdle, updatePauseState]);

  // Tab visibility — IdleTimeContext doesn't track this.
  useEffect(() => {
    const handler = () =>
      updatePauseState(isIdleRef.current || document.hidden);
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [updatePauseState]);

  // Sleep / JS-suspension gap detection.
  // If a 1 s tick sees a wall-clock gap > SLEEP_GAP_MS, the engine was paused.
  // Retroactively push a pause interval — but only when the pause machine
  // didn't already capture it (e.g. visibilitychange fired on wake).
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const gap = now - lastTickRef.current;

      if (gap > SLEEP_GAP_MS && !isPausedRef.current) {
        const last = pauseIntervalsRef.current.at(-1);
        const alreadyCovered = last != null && last.end >= lastTickRef.current;
        if (!alreadyCovered) {
          pauseIntervalsRef.current.push({
            start: lastTickRef.current,
            end: now,
          });
        }
      }

      lastTickRef.current = now;
    }, 1_000);

    return () => clearInterval(interval);
  }, []);

  // Window blur / focus — catches alt-tab to another app before idle kicks in.
  // Tracked separately from the pause machine: blur records the away period,
  // and on focus we commit the graced interval to pauseIntervalsRef. (If we
  // only read blurStartRef at getTimingData time, the away-time would be lost
  // on focus, since the window must be focused to submit.)
  useEffect(() => {
    const handleBlur = () => {
      blurStartRef.current = Date.now();
    };
    const handleFocus = () => {
      if (blurStartRef.current !== null) {
        const start = blurStartRef.current + WINDOW_BLUR_GRACE_MS;
        const end = Date.now();
        if (start < end) {
          pauseIntervalsRef.current.push({ start, end });
        }
      }
      blurStartRef.current = null;
    };
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const recordQ1Interaction = useCallback(() => {
    if (!q2FirstTouchedRef.current) {
      q1CheckpointRef.current = Date.now();
    }
  }, []);

  const recordQ2Interaction = useCallback(() => {
    q2FirstTouchedRef.current = true;
    if (!q3FirstTouchedRef.current) {
      q2CheckpointRef.current = Date.now();
    }
  }, []);

  const recordQ3Interaction = useCallback(() => {
    q3FirstTouchedRef.current = true;
    q3CheckpointRef.current = Date.now();
  }, []);

  const getTimingData = useCallback((): TimingData => {
    const now = Date.now();
    const taskStart = taskStartRef.current;

    // Collect all pause sources.
    const allIntervals: Interval[] = [...pauseIntervalsRef.current];

    // Current open pause-machine pause.
    if (isPausedRef.current && pauseStartRef.current !== null) {
      allIntervals.push({ start: pauseStartRef.current, end: now });
    }

    // Window-blur period beyond the grace window.
    if (blurStartRef.current !== null) {
      const gracedStart = blurStartRef.current + WINDOW_BLUR_GRACE_MS;
      if (gracedStart < now) {
        allIntervals.push({ start: gracedStart, end: now });
      }
    }

    // Merge overlapping intervals so no period is double-subtracted.
    const merged = mergeIntervals(
      allIntervals.sort((a, b) => a.start - b.start),
    );

    const pausedInRange = (from: number, to: number) =>
      merged.reduce((sum, { start, end }) => {
        const s = Math.max(start, from);
        const e = Math.min(end, to);
        return sum + Math.max(0, e - s);
      }, 0);

    const q1End = q1CheckpointRef.current ?? taskStart;
    const q2End = q2CheckpointRef.current ?? q1End;
    const q3End = q3CheckpointRef.current ?? q2End;

    return {
      total_duration: Math.max(
        0,
        now - taskStart - pausedInRange(taskStart, now),
      ),
      q1_duration: Math.max(
        0,
        q1End - taskStart - pausedInRange(taskStart, q1End),
      ),
      q2_duration: Math.max(0, q2End - q1End - pausedInRange(q1End, q2End)),
      q3_duration: Math.max(0, q3End - q2End - pausedInRange(q2End, q3End)),
      review_duration: Math.max(0, now - q3End - pausedInRange(q3End, now)),
    };
  }, []);

  return (
    <TimeTrackingContext.Provider
      value={{
        recordQ1Interaction,
        recordQ2Interaction,
        recordQ3Interaction,
        getTimingData,
      }}
    >
      {children}
    </TimeTrackingContext.Provider>
  );
}

const NO_OP_CONTEXT: TimeTrackingContextValue = {
  recordQ1Interaction: () => {},
  recordQ2Interaction: () => {},
  recordQ3Interaction: () => {},
  getTimingData: () => null,
};

export function useTimeTracking(): TimeTrackingContextValue {
  return useContext(TimeTrackingContext) ?? NO_OP_CONTEXT;
}
